import 'swiper/css/pagination';

import clsx from 'clsx';
import {useCallback} from 'react';
import {Pagination} from 'swiper/modules';
import {SwiperSlide} from 'swiper/react';

import SwiperCarousel from '@/components/Swiper/SwiperCarousel';
import type {TipsInstruction} from '@/services/content/tipsContent.types';
import eventMessagesService from '@/services/events/eventMessagesService';

import getSwiperOptions from '../getSwiperOptions';
import {useMaxInstructionHeight} from '../hooks/useMaxInstructionHeight';
import InstructionContent from './InstructionContent';

type DetailViewProps = {
    instructions: TipsInstruction[];
    guideItemType?: string;
    onInstructionChange?: (instruction: TipsInstruction) => void;
    /** Image above text; swipe still covers full popup. */
    splitImageLayout?: boolean;
    onMaxInstructionHeightChange?: (height: number) => void;
};

const DetailView = ({
    instructions,
    guideItemType,
    onInstructionChange,
    splitImageLayout = false,
    onMaxInstructionHeightChange,
}: DetailViewProps) => {
    const hasMultipleSlides = instructions.length > 1;

    const handlePrimaryButtonClick = useCallback(
        (action?: string, trackingValue?: string) => {
            eventMessagesService.trackAmplitude(trackingValue);
            if (action) {
                eventMessagesService.sendButtonAction(action, {guideItemType});
            }
        },
        [guideItemType]
    );

    const handleSlideChange = useCallback(
        (swiper: {activeIndex: number}) => {
            const instruction = instructions[swiper.activeIndex];
            if (instruction) onInstructionChange?.(instruction);
        },
        [instructions, onInstructionChange]
    );

    const {setItemRef} = useMaxInstructionHeight({
        enabled: splitImageLayout,
        itemCount: instructions.length,
        onChange: onMaxInstructionHeightChange,
    });

    const renderInstruction = useCallback(
        (step: TipsInstruction, index: number) => (
            <div
                ref={splitImageLayout ? setItemRef(index) : undefined}
                className={clsx('w-full', splitImageLayout && 'shrink-0')}
            >
                <InstructionContent
                    step={step}
                    hasMultipleSlides={hasMultipleSlides}
                    onPrimaryButtonClick={handlePrimaryButtonClick}
                    overlapsMedia={!splitImageLayout}
                />
            </div>
        ),
        [handlePrimaryButtonClick, hasMultipleSlides, setItemRef, splitImageLayout]
    );

    if (!hasMultipleSlides) {
        const step = instructions[0];
        if (!step) return null;

        return (
            <div
                className={clsx(
                    'w-full',
                    splitImageLayout && 'flex h-full min-h-0 flex-col justify-end'
                )}
            >
                {renderInstruction(step, 0)}
            </div>
        );
    }

    return (
        <div className={clsx('flex min-h-0 w-full flex-1', splitImageLayout && 'h-full')}>
            <SwiperCarousel
                options={{
                    ...getSwiperOptions(true),
                    ...(splitImageLayout
                        ? {onSlideChangeTransitionEnd: handleSlideChange}
                        : {onSlideChange: handleSlideChange}),
                }}
                modules={[Pagination]}
                className={clsx(
                    'instruction-swiper instruction-carousel min-h-0 w-full flex-1',
                    splitImageLayout && 'h-full'
                )}
            >
                {instructions.map((step, i) => (
                    <SwiperSlide
                        key={i}
                        className={clsx(
                            'flex h-full w-full flex-col',
                            !splitImageLayout && 'justify-end'
                        )}
                    >
                        {splitImageLayout && <div className='min-h-0 flex-1' aria-hidden='true' />}
                        {renderInstruction(step, i)}
                    </SwiperSlide>
                ))}
            </SwiperCarousel>
        </div>
    );
};

export default DetailView;
